import { api } from './api';

export interface AdminStoryDto {
  id: string;
  title: string;
  summary: string;
  authorId: string;
  genre: string;
  status: 'PENDING_REVIEW' | 'AI_REVIEW_PENDING' | 'ADMIN_REVIEW_PENDING' | 'APPROVED' | 'REJECTED' | 'REVISION_REQUESTED' | 'APPEAL_PENDING';
  statusDisplayName: string;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  hasAiAnalysis: boolean;
  aiAnalysis?: {
    confidence: number;
    appropriate: boolean;
    violations: string[];
    explanation: string;
    modelVersion: string;
    analyzedAt: string;
  };
  urgency: 'high' | 'medium' | 'low';
  content: string;
}

export interface QueueStats {
  totalPending: number;
  aiReviewPending: number;
  adminReviewPending: number;
  appealPending: number;
  totalRejected: number;
  totalApproved: number;
  urgent: number;
}

export interface ReviewHistoryItem {
  id: number;
  action: 'APPROVE' | 'REJECT' | 'REQUEST_CHANGES';
  reviewNotes: string;
  reviewedAt: string;
  isAutomated: boolean;
  reviewerId?: string;
  previousStatus: string;
  newStatus: string;
}

export const adminStoryService = {
  async getPendingStories(params: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
    status?: string;
  } = {}) {
    const queryParams = new URLSearchParams({
      page: (params.page || 0).toString(),
      size: (params.size || 20).toString(),
      sortBy: params.sortBy || 'submittedForReviewAt',
      sortDir: params.sortDir || 'asc',
      ...(params.status && { status: params.status })
    });

    const response = await api.get(`/admin/stories/pending?${queryParams}`);
    return response.data;
  },

  async getQueueStats(): Promise<QueueStats> {
    const response = await api.get('/admin/stories/queue-stats');
    return response.data;
  },

  async approveStory(storyId: string, notes?: string) {
    const response = await api.post(`/admin/stories/${storyId}/approve`, {
      notes: notes || 'Approved by admin'
    });
    return response.data;
  },

  async rejectStory(storyId: string, reason: string) {
    const response = await api.post(`/admin/stories/${storyId}/reject`, {
      reason
    });
    return response.data;
  },

  async requestRevision(storyId: string, revisionNotes: string) {
    const response = await api.post(`/admin/stories/${storyId}/request-revision`, {
      revisionNotes
    });
    return response.data;
  },

  async getStoryDetails(storyId: string): Promise<AdminStoryDto> {
    const response = await api.get(`/admin/stories/${storyId}`);
    return response.data;
  },

  async getStoryAiAnalysis(storyId: string) {
    const response = await api.get(`/admin/stories/${storyId}/analysis`);
    return response.data;
  },

  async getStoryReviewHistory(storyId: string): Promise<ReviewHistoryItem[]> {
    const response = await api.get(`/admin/stories/${storyId}/review-history`);
    return response.data;
  },

  async triggerAiReview(storyId: string) {
    const response = await api.post(`/admin/stories/${storyId}/trigger-ai-review`);
    return response.data;
  }
};